import React, { useMemo, useState } from 'react';
import qs from 'qs';
import { useNavigate } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';
import { useManagementStore, useSettingStore, useUserState } from '../../store';
import axios from '../../utilities/axios';
import SHOW_TRADE_IMPORT from '../../utilities/featureFlags';
import { FORMAT } from '../../utilities/functions';

import './styles.scss';

const VIEW = {
    MONTH: 'month',
    YEAR: 'year'
};

const WEEKDAYS_MONTH = ['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_YEAR = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function startOfWeekMonday(d) {
    // dayjs().day(): 0 (Sun) - 6 (Sat)
    const diff = (d.day() + 6) % 7;
    return d.subtract(diff, 'day');
}

function buildMonthAggFromDays(dayMap) {
    // dayMap shape: { 'YYYY-MM-DD': [ { totalNetPnLPerDay, totalTradesPerDay } ] }
    const monthAgg = {};

    if (!dayMap) {
        return monthAgg;
    }

    Object.entries(dayMap).forEach(([dateKey, arr]) => {
        const row = arr && arr[0];

        if (!row) {
            return;
        }

        const monthKey = dateKey.slice(0, 7); // YYYY-MM

        if (!monthAgg[monthKey]) {
            monthAgg[monthKey] = { totalNetPnLPerMonth: 0, totalTradesPerMonth: 0 };
        }

        monthAgg[monthKey].totalNetPnLPerMonth += Number(row.totalNetPnLPerDay || 0);
        monthAgg[monthKey].totalTradesPerMonth += Number(row.totalTradesPerDay || 0);
    });

    return monthAgg;
}

function chunkWeeks(days) {
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
}

export default function CalendarPage() {
    const { filter } = useSettingStore((state) => state);
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [view, setView] = useState(VIEW.MONTH);
    const { publicView } = useManagementStore((state) => state);
    const { getUser } = useUserState();
    const { darkMode } = getUser();
    const navigate = useNavigate();

    const modifiedFilter = { ...filter, dateRange: null, symbols: [], getAllofTickers: true };

    const fetchAccountCalendar = (inputFilter) =>
        axios
            .get('stats/calendar', {
                params: {
                    accounts: inputFilter.accounts,
                    tags: inputFilter.tags,
                    showAllTags: inputFilter.showAllTags,
                    symbols: inputFilter.symbols,
                    status: inputFilter.status,
                    side: inputFilter.side,
                    assetType: inputFilter.assetType,
                    getAllofTickers: inputFilter.getAllofTickers
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                let responseData = null;
                if (response.status === 200) {
                    responseData = response.data;
                }

                return responseData;
            })
            .catch((error) => {
                console.error('Error:', error);
            });

    const { isLoading, data: calendarData } = useQuery(['calendar-account', filter], () =>
        fetchAccountCalendar(publicView.isActive ? modifiedFilter : filter)
    );

    const dayMap = calendarData?.day || null;
    const monthAgg = useMemo(() => buildMonthAggFromDays(dayMap), [dayMap]);

    const monthStart = currentDate.startOf('month');
    const yearStart = currentDate.startOf('year');

    const monthGridDays = useMemo(() => {
        // 6 weeks x 7 days (Mon - Sun)
        const start = startOfWeekMonday(monthStart);
        const days = [];

        for (let i = 0; i < 42; i += 1) {
            days.push(start.add(i, 'day'));
        }

        return days;
    }, [monthStart]);

    const monthWeeks = useMemo(() => chunkWeeks(monthGridDays), [monthGridDays]);

    const monthOptions = useMemo(
        () =>
            Array.from({ length: 12 }).map((_, i) => ({
                value: i,
                label: dayjs().month(i).format('MMMM')
            })),
        []
    );

    const yearOptions = useMemo(() => {
        const keys = dayMap ? Object.keys(dayMap) : [];
        const years = keys.map((k) => Number(k.slice(0, 4))).filter((y) => Number.isFinite(y));

        const currentYear = currentDate.year();
        const min = years.length ? Math.min(...years) : currentYear - 5;
        const max = years.length ? Math.max(...years) : currentYear + 5;
        const paddedMin = Math.min(min, currentYear - 2);
        const paddedMax = Math.max(max, currentYear + 2);

        const out = [];
        for (let y = paddedMin; y <= paddedMax; y += 1) out.push(y);
        return out;
    }, [dayMap, currentDate]);

    const onSelectMonth = (monthIndex) => {
        setCurrentDate((d) => d.month(monthIndex).startOf('month'));
        setView(VIEW.MONTH);
    };

    const onSelectYear = (year) => {
        setCurrentDate((d) => d.year(year).startOf('month'));
    };

    const yearTotals = useMemo(() => {
        let pnl = 0;
        let trades = 0;

        for (let i = 0; i < 12; i += 1) {
            const key = yearStart.add(i, 'month').format('YYYY-MM');
            const row = monthAgg[key];

            if (row) {
                pnl += Number(row.totalNetPnLPerMonth || 0);
                trades += Number(row.totalTradesPerMonth || 0);
            }
        }

        return { pnl, trades };
    }, [monthAgg, yearStart]);

    if (isLoading) return <></>;

    if (!calendarData) {
        return (
            <div className="no-data flex flex-col gap-2">
                <div className="text-2xl font-semibold">No data available.</div>
                <div className="text-sm text-muted-foreground">
                    Select different filters or upload trade logs by using the{' '}
                    <strong>Import Trades</strong> button.
                </div>
            </div>
        );
    }

    return (
        <div
            className={
                darkMode
                    ? 'dark bg-background text-foreground w-full h-full flex flex-col flex-1 self-stretch'
                    : 'bg-background text-foreground w-full h-full flex flex-col flex-1 self-stretch'
            }
            style={
                darkMode
                    ? {
                          '--background': '0 0% 3.9%',
                          '--foreground': '0 0% 98%',
                          '--card': '0 0% 9%',
                          '--card-foreground': '0 0% 98%',
                          '--muted': '0 0% 15%',
                          '--muted-foreground': '0 0% 64%',
                          '--border': '0 0% 100%',
                          '--input': '0 0% 100%',
                          '--primary': '217 91% 60%',
                          '--primary-foreground': '0 0% 98%'
                      }
                    : undefined
            }
        >
            <div className="w-full flex-1 self-stretch px-3 py-4 sm:px-6 sm:py-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-3xl font-bold tracking-tight">Calendar</div>

                    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
                        <Select
                            value={String(currentDate.month())}
                            onValueChange={(value) => onSelectMonth(Number(value))}
                        >
                            <SelectTrigger
                                aria-label="Select month"
                                className="w-full sm:w-[153px]"
                            >
                                <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent>
                                {monthOptions.map((m) => (
                                    <SelectItem key={String(m.value)} value={String(m.value)}>
                                        {m.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select
                            value={String(currentDate.year())}
                            onValueChange={(value) => onSelectYear(Number(value))}
                        >
                            <SelectTrigger aria-label="Select year" className="w-full sm:w-[91px]">
                                <SelectValue placeholder="Select year" />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map((y) => (
                                    <SelectItem key={String(y)} value={String(y)}>
                                        {y}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <ToggleGroup
                            type="single"
                            value={view}
                            onValueChange={(v) => v && setView(v)}
                            aria-label="Calendar view"
                            className="h-9 w-full border-0 bg-muted p-[3px] sm:w-[212px] flex items-center toggleView"
                        >
                            <ToggleGroupItem
                                value={VIEW.MONTH}
                                aria-label="Month view"
                                size="sm"
                                className="flex-1 flex items-center data-[state=on]:shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]"
                            >
                                Month
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value={VIEW.YEAR}
                                aria-label="Year view"
                                size="sm"
                                className="flex-1 flex items-center data-[state=on]:shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]"
                            >
                                Year
                            </ToggleGroupItem>
                        </ToggleGroup>

                        {SHOW_TRADE_IMPORT && !publicView.isActive && (
                            <Button
                                size="sm"
                                style={{ backgroundColor: '#2563EB' }}
                                onClick={() => navigate('/add-trade')}
                            >
                                Import
                            </Button>
                        )}
                    </div>
                </div>

                {view === VIEW.YEAR && (
                    <div className="mt-2 text-sm text-muted-foreground">
                        {FORMAT.NUMBER_TO_CURRENCY(yearTotals.pnl)} · {yearTotals.trades} trades
                    </div>
                )}

                {view === VIEW.MONTH && (
                    <div className="mt-4 flex-1">
                        <Card className="overflow-hidden bg-background">
                            <div className="grid grid-cols-7">
                                {WEEKDAYS_MONTH.map((d, idx) => (
                                    <div
                                        key={d}
                                        className={[
                                            'flex items-center justify-center p-2',
                                            'bg-background',
                                            'text-xs font-medium text-muted-foreground',
                                            'border-b border-border',
                                            idx === 6 ? '' : 'border-r border-border'
                                        ].join(' ')}
                                    >
                                        {d}
                                    </div>
                                ))}
                            </div>

                            {monthWeeks.map((week, wi) => (
                                <div key={String(wi)} className="grid grid-cols-7">
                                    {week.map((d, di) => {
                                        const inMonth = d.month() === currentDate.month();
                                        const key = d.format('YYYY-MM-DD');
                                        const row = dayMap?.[key]?.[0] || null;
                                        const pnl = row ? Number(row.totalNetPnLPerDay || 0) : 0;
                                        const trades = row ? Number(row.totalTradesPerDay || 0) : 0;

                                        const isPositive = pnl > 0;
                                        const isNegative = pnl < 0;
                                        const showEvent = !!row;

                                        const cellClassName = [
                                            'flex flex-col gap-1 p-2',
                                            'min-h-[108px] sm:min-h-[128px]',
                                            wi === monthWeeks.length - 1
                                                ? ''
                                                : 'border-b border-border',
                                            di === 6 ? '' : 'border-r border-border',
                                            inMonth ? 'bg-background' : 'bg-muted'
                                        ]
                                            .filter(Boolean)
                                            .join(' ');

                                        const eventClassName = [
                                            'w-full flex-1 rounded-[6px] border px-2 py-1.5',
                                            !isPositive && !isNegative
                                                ? 'border-border bg-muted/20 text-foreground'
                                                : '',
                                            isPositive
                                                ? 'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
                                                : '',
                                            isNegative
                                                ? 'border-red-600 bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300'
                                                : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ');

                                        return (
                                            <div key={key} className={cellClassName}>
                                                <div className="h-6 w-6 rounded-full flex items-center justify-start pl-1">
                                                    <span
                                                        className={
                                                            inMonth
                                                                ? 'text-xs text-foreground'
                                                                : 'text-xs text-muted-foreground'
                                                        }
                                                    >
                                                        {d.date()}
                                                    </span>
                                                </div>

                                                {showEvent && (
                                                    <div
                                                        className={`${eventClassName} text-left space-y-1.5`}
                                                    >
                                                        <div className="text-[14px] font-medium leading-[28px] truncate">
                                                            {trades}{' '}
                                                            {trades === 1 ? 'Trade' : 'Trades'}
                                                        </div>
                                                        <div className="text-[18px] font-medium leading-[28px] truncate !mt-0">
                                                            {FORMAT.NUMBER_TO_CURRENCY(pnl)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </Card>
                    </div>
                )}

                {view === VIEW.YEAR && (
                    <div className="mt-4 flex-1">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {Array.from({ length: 12 }).map((_, i) => {
                                const m = yearStart.add(i, 'month');
                                const monthKey = m.format('YYYY-MM');
                                const monthDays = chunkWeeks(
                                    Array.from({ length: 42 }).map((__, di) =>
                                        m.startOf('month').startOf('week').add(di, 'day')
                                    )
                                );

                                return (
                                    <Card
                                        key={monthKey}
                                        className="rounded-lg border border-neutral-200 bg-background p-2 shadow-none dark:border-neutral-800"
                                    >
                                        <button
                                            type="button"
                                            className="w-full text-left"
                                            onClick={() => {
                                                setCurrentDate(m.startOf('month'));
                                                setView(VIEW.MONTH);
                                            }}
                                        >
                                            <div className="flex items-center justify-center pb-1">
                                                <div className="h-6 px-2 grid place-items-center rounded font-bold text-xs text-foreground">
                                                    {m.format('MMMM')}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-7 justify-items-center gap-y-1 text-xs font-normal">
                                                {WEEKDAYS_YEAR.map((wd, idx) => (
                                                    <div
                                                        key={wd}
                                                        className={[
                                                            'h-6 w-6 grid place-items-center rounded',
                                                            idx === 0 || idx === 6
                                                                ? 'text-blue-600 dark:text-blue-400'
                                                                : 'text-foreground'
                                                        ].join(' ')}
                                                    >
                                                        {wd}
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-1 space-y-1">
                                                {monthDays.map((week, wi) => (
                                                    <div
                                                        key={String(wi)}
                                                        className="grid grid-cols-7 justify-items-center gap-y-1"
                                                    >
                                                        {week.map((d) => {
                                                            const inMonth = d.month() === m.month();
                                                            const isWeekend =
                                                                d.day() === 0 || d.day() === 6;
                                                            const key = d.format('YYYY-MM-DD');
                                                            const row = inMonth
                                                                ? dayMap?.[key]?.[0] || null
                                                                : null;
                                                            const pnl = row
                                                                ? Number(row.totalNetPnLPerDay || 0)
                                                                : 0;

                                                            const isPositive = pnl > 0;
                                                            const isNegative = pnl < 0;

                                                            let textClassName = 'text-foreground';
                                                            if (isPositive || isNegative) {
                                                                textClassName = 'text-foreground';
                                                            } else if (!inMonth) {
                                                                textClassName = isWeekend
                                                                    ? 'text-blue-600 dark:text-blue-400'
                                                                    : 'text-muted-foreground';
                                                            } else if (isWeekend) {
                                                                textClassName =
                                                                    'text-blue-600 dark:text-blue-400';
                                                            }

                                                            const dayClassName = [
                                                                'h-6 w-6 rounded grid place-items-center text-xs',
                                                                textClassName,
                                                                isPositive
                                                                    ? 'bg-blue-50 border border-blue-600 dark:bg-blue-950'
                                                                    : '',
                                                                isNegative
                                                                    ? 'bg-red-50 border border-red-600 dark:bg-red-950'
                                                                    : ''
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ');

                                                            return (
                                                                <div
                                                                    key={key}
                                                                    className={dayClassName}
                                                                >
                                                                    {d.date()}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))}
                                            </div>
                                        </button>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
